import {
  createBrowserRouter,
  RouterProvider,
  Outlet,
  Link,
  useNavigate,
  useLocation,
} from "react-router";
import {
  ArrowUpRight,
  Building2,
  CreditCard,
  Home,
  KeyRound,
  LogOut,
  Network,
  UserRound,
} from "lucide-react";
import { ProtectedRoute } from "@/account/components/ProtectedRoute";
import { ErrorBoundary } from "@/account/components/ErrorBoundary";
import { WorldSiteHeader } from "@/account/components/WorldSiteHeader";
import { signOut, useSession } from "@/account/lib/auth-client";
import { trpc } from "@/account/lib/trpc";
import { TRPCProvider } from "@/account/providers/TRPCProvider";
import { Login } from "@/account/pages/Login";
import { SignUp } from "@/account/pages/SignUp";
import { Logout } from "@/account/pages/Logout";
import { MagicLinkConfirm } from "@/account/pages/MagicLinkConfirm";
import { HomePage } from "@/account/pages/HomePage";
import { Account } from "@/account/pages/Account";
import { OrganizationsPage } from "@/account/pages/OrganizationsPage";
import { OrganizationPage } from "@/account/pages/OrganizationPage";
import { CreateOrganization } from "@/account/pages/CreateOrganization";
import { SubscriptionPage } from "@/account/pages/SubscriptionPage";
import { SubscriptionUpgrade } from "@/account/pages/SubscriptionUpgrade";
import { AcceptInvitation } from "@/account/pages/AcceptInvitation";
import { SubscriptionUpdated } from "@/account/pages/SubscriptionUpdated";
import { NewSubscriptionPage } from "@/account/pages/NewSubscriptionPage";
import { SubscriptionCreated } from "@/account/pages/SubscriptionCreated";
import { SubPeriodPage } from "@/account/pages/SubPeriodPage";
import { SubPeriodsPage } from "@/account/pages/SubPeriodsPage";
import {SubscriptionsPage} from "@/account/pages/SubscriptionsPage";
import {ApiAccessPage} from "@/account/pages/ApiAccessPage";

function BrandFooter() {
  return (
    <footer className="brand-footer" aria-label="NOSIBLE account footer">
      <div className="brand-footer__base">
        <a href="https://nosible.com">NOSIBLE / ACCOUNT</a>
        <nav aria-label="NOSIBLE account footer links">
          <a href="https://nosible.com">NOSIBLE.COM</a>
          <a href="https://nosible.world/world">WORLD</a>
          <a href="https://docs.nosible.com">DOCS</a>
          <a href="https://nosible.com/legal/privacy">PRIVACY</a>
        </nav>
        <span>© NOSIBLE 2026</span>
      </div>
    </footer>
  );
}


function PublicHeader() {
  return <WorldSiteHeader />;
}

function PublicLayout() {
  const { pathname } = useLocation();
  const isMinimalAuthRoute = pathname === "/login" || pathname === "/confirm-login";

  return (
    <div className={isMinimalAuthRoute ? "account-public account-public--minimal" : "account-public"}>
      <a className="skip-link" href="#main-content">SKIP TO CONTENT</a>
      {!isMinimalAuthRoute ? <PublicHeader /> : null}
      <main id="main-content"><Outlet /></main>
      {!isMinimalAuthRoute ? <BrandFooter /> : null}
    </div>
  );
}

function AccountHeader({ onLogout }: { onLogout: () => Promise<void> }) {
  return <WorldSiteHeader onLogout={onLogout} />;
}

function AccountSidebar({ onLogout }: { onLogout: () => Promise<void> }) {
  const { data: session } = useSession();
  const { pathname } = useLocation();
  const { data: profile } = trpc.user.getProfile.useQuery(undefined, {
    enabled: Boolean(session),
  });

  const identity = session?.user?.name?.trim() || session?.user?.email?.trim() || "NOSIBLE USER";
  const initials = identity
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
  const organizations = profile?.organizations ?? [];
  const subscriptions = (profile?.subscriptions ?? []).filter((subscription) => {
    const status = subscription.status.toLowerCase();
    return status !== "canceled" && status !== "cancelled";
  });

  const isCurrent = (to: string) => to === "/" ? pathname === "/" : pathname === to || pathname.startsWith(`${to}/`);

  return (
    <aside className="account-sidebar" aria-label="Account navigation">
      <div className="account-sidebar__top">
        <div className="account-sidebar__identity">
          <span className="account-sidebar__avatar" aria-hidden="true">{initials || "SR"}</span>
          <span className="account-sidebar__identity-copy">
            <strong>{identity}</strong>
            <span>{session?.user?.email}</span>
          </span>
        </div>

        <nav className="account-sidebar__nav">
          <p className="account-sidebar__label">ACCOUNT</p>
          <Link className={isCurrent("/") ? "is-current" : undefined} to="/">
            <Home aria-hidden="true" />
            <span>Overview</span>
          </Link>
          <Link className={isCurrent("/account") ? "is-current" : undefined} to="/account">
            <UserRound aria-hidden="true" />
            <span>Profile &amp; email</span>
          </Link>
          <Link className={isCurrent("/organizations") ? "is-current" : undefined} to="/organizations">
            <Network aria-hidden="true" />
            <span>Organizations</span>
            <small>{organizations.length.toString().padStart(2, "0")}</small>
          </Link>
          <div className="account-sidebar__nested">
            {organizations.map((organization) => (
              <Link
                key={organization.id}
                className={isCurrent(`/organizations/${organization.slug}`) ? "is-current" : undefined}
                to={`/organizations/${organization.slug}`}
              >
                <span>{organization.name}</span>
                <small>{organization.role.toUpperCase()}</small>
              </Link>
            ))}
          </div>

          <Link className={isCurrent("/subscriptions") ? "is-current" : undefined} to="/subscriptions">
            <CreditCard aria-hidden="true" />
            <span>Subscriptions</span>
            <small>{subscriptions.length.toString().padStart(2, "0")}</small>
          </Link>
          <div className="account-sidebar__nested">
            {subscriptions.map((subscription) => (
              <Link
                key={subscription.id}
                className={isCurrent(`/subscriptions/${subscription.id}`) ? "is-current" : undefined}
                to={`/subscriptions/${subscription.id}`}
              >
                <span>{subscription.name || subscription.plan.name}</span>
                <small>{subscription.status.toUpperCase()}</small>
              </Link>
            ))}
          </div>
        </nav>
      </div>

      <div className="account-sidebar__footer">
        <Link className={isCurrent("/api-access") ? "is-current" : undefined} to="/api-access">
          <KeyRound aria-hidden="true" />
              <span>Your API Keys</span>
          <ArrowUpRight aria-hidden="true" />
        </Link>
        <button type="button" onClick={() => void onLogout()}>
          <LogOut aria-hidden="true" />
          <span>SIGN OUT</span>
        </button>
      </div>
    </aside>
  );
}

function ClientLayout() {
  const navigate = useNavigate();

  const handleLogout = async () => {
    await signOut({
      fetchOptions: {
        onSuccess: () => {
          if (window.location.hostname !== "app.nosible.com") {
            navigate("/login");
            return;
          }
          const logout = new URL("https://nosible.world/api/auth/world/logout");
          logout.searchParams.set("return_to", `${window.location.origin}/login`);
          window.location.assign(logout.toString());
        },
      },
    });
  };

  return (
    <ProtectedRoute>
      <div className="account-shell">
        <a className="skip-link" href="#main-content">SKIP TO CONTENT</a>
        <AccountHeader onLogout={handleLogout} />
        <div className="account-private-layout">
          <AccountSidebar onLogout={handleLogout} />
          <main id="main-content" className="account-route"><Outlet /></main>
        </div>
      </div>
    </ProtectedRoute>
  );
}

const router = createBrowserRouter([
  {
    Component: PublicLayout,
    ErrorBoundary,
    children: [
      { path: "/login", Component: Login },
      { path: "/sign-up", Component: SignUp },
      { path: "/logout", Component: Logout },
      { path: "/confirm-login", Component: MagicLinkConfirm },
      { path: "/accept-invitation/:invitationId", Component: AcceptInvitation },
    ],
  },
  {
    path: "/",
    Component: ClientLayout,
    ErrorBoundary,
    children: [
      { index: true, Component: HomePage },
      { path: "account", Component: Account },
      { path: "organizations", Component: OrganizationsPage },
      { path: "organizations/create", Component: CreateOrganization },
      { path: "organizations/:slug", Component: OrganizationPage },
      { path: "subscriptions", Component: SubscriptionsPage },
      { path: "api-access", Component: ApiAccessPage },
      { path: "subscriptions/:subscriptionId", Component: SubscriptionPage },
      { path: "subscriptions/:subscriptionId/upgrade", Component: SubscriptionUpgrade },
      { path: "subscriptions/:subscriptionId/success", Component: SubscriptionCreated },
      { path: "subscriptions/:subscriptionId/updated", Component: SubscriptionUpdated },
      { path: "subscriptions/:subscriptionId/periods", Component: SubPeriodsPage },
      { path: "subscriptions/:subscriptionId/period/:periodId", Component: SubPeriodPage },
      { path: "new-subscription", Component: NewSubscriptionPage },
      { path: "organizations/:slug/new-subscription", Component: NewSubscriptionPage },
    ],
  },
]);

export function AccountApp() {
  return (
    <TRPCProvider>
      <RouterProvider router={router} />
    </TRPCProvider>
  );
}
